function createGlobalThisVariable(name, defaultValue) {
  if (!globalThis.vonage) {
    globalThis.vonage = {};
  }
  if (!globalThis.vonage.workerizer) {
    globalThis.vonage.workerizer = {};
  }
  let globals2 = globalThis.vonage.workerizer;
  if (!globals2[name]) {
    globals2[name] = defaultValue;
  }
  return globals2[name];
}
const globals = createGlobalThisVariable(
  "globals",
  {}
);
var CommandType = /* @__PURE__ */ ((CommandType2) => {
  CommandType2["INIT"] = "INIT";
  CommandType2["FORWARD"] = "FORWARD";
  CommandType2["TERMINATE"] = "TERMINATE";
  CommandType2["GLOBALS_SYNC"] = "GLOBALS_SYNC";
  CommandType2["EVENT"] = "EVENT";
  return CommandType2;
})(CommandType || {});
function isTransferable(arg) {
  const transferableTypes = [ImageBitmap, ReadableStream, WritableStream];
  return transferableTypes.some((type) => arg instanceof type);
}
let nextCommandId = 0;
function postCommand$1(worker, type, functionName, args, resolvers) {
  const id = nextCommandId++;
  worker.postMessage(
    {
      id,
      type,
      functionName,
      args
    },
    args.filter((a) => isTransferable(a))
  );
  const promise = new Promise((resolve) => {
    resolvers == null ? void 0 : resolvers.set(id, resolve);
  });
  return promise;
}
function postCommand(command, result) {
  const { id, type } = command;
  const resultAsArray = Array.isArray(result) ? result : [result];
  postMessage(
    {
      id,
      type,
      result
    },
    resultAsArray.filter((result2) => isTransferable(result2))
  );
}
const workerized = createGlobalThisVariable("workerized", {});
function isWorker() {
  return typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope;
}
async function globalsSync() {
  if (isWorker()) {
    postCommand({ type: CommandType.GLOBALS_SYNC }, globals);
  } else {
    const promises = [];
    for (const i in workerized) {
      const { worker, resolvers } = workerized[i].workerContext;
      if (worker) {
        promises.push(
          postCommand$1(
            worker,
            CommandType.GLOBALS_SYNC,
            "",
            [globals],
            resolvers
          )
        );
      }
    }
    await Promise.all(promises);
  }
}
function copy(from, to) {
  if (Array.isArray(to)) {
    to.splice(0, to.length);
  } else if (typeof to === "object") {
    for (const i in to) {
      delete to[i];
    }
  }
  for (const i in from) {
    if (Array.isArray(from[i])) {
      to[i] = [];
      copy(from[i], to[i]);
    } else if (typeof from[i] === "object") {
      to[i] = {};
      copy(from[i], to[i]);
    } else {
      to[i] = from[i];
    }
  }
}
async function createWorker(workerizedClass, workerClass, resolvers, onEvent) {
  const worker = new workerClass();
  worker.addEventListener(
    "message",
    async ({ data }) => {
      var _a, _b, _c, _d, _e, _f, _g;
      switch (data.type) {
        case CommandType.GLOBALS_SYNC:
          if (data.id) {
            (_b = resolvers.get((_a = data.id) != null ? _a : -1)) == null ? void 0 : _b(data.result);
            resolvers.delete((_c = data.id) != null ? _c : -1);
          } else {
            copy((_d = data.result) != null ? _d : {}, globals);
            await globalsSync();
          }
          break;
        case CommandType.EVENT:
          const { result } = data;
          const event = result;
          if ((event == null ? void 0 : event.name) == void 0) {
            throw "Missing event name";
          }
          onEvent(event.name, event.data);
          break;
        default:
          (_f = resolvers.get((_e = data.id) != null ? _e : -1)) == null ? void 0 : _f(data.result);
          resolvers.delete((_g = data.id) != null ? _g : -1);
      }
    }
  );
  const initialized = await postCommand$1(
    worker,
    CommandType.INIT,
    "",
    [workerizedClass.workerId, globals],
    resolvers
  );
  if (!initialized) {
    throw "Failed to instantiate workerized class";
  }
  return worker;
}
const anyMap = /* @__PURE__ */ new WeakMap();
const eventsMap = /* @__PURE__ */ new WeakMap();
const producersMap = /* @__PURE__ */ new WeakMap();
const anyProducer = Symbol("anyProducer");
const resolvedPromise = Promise.resolve();
const listenerAdded = Symbol("listenerAdded");
const listenerRemoved = Symbol("listenerRemoved");
let canEmitMetaEvents = false;
let isGlobalDebugEnabled = false;
function assertEventName(eventName) {
  if (typeof eventName !== "string" && typeof eventName !== "symbol" && typeof eventName !== "number") {
    throw new TypeError("`eventName` must be a string, symbol, or number");
  }
}
function assertListener(listener) {
  if (typeof listener !== "function") {
    throw new TypeError("listener must be a function");
  }
}
function getListeners(instance, eventName) {
  const events = eventsMap.get(instance);
  if (!events.has(eventName)) {
    return;
  }
  return events.get(eventName);
}
function getEventProducers(instance, eventName) {
  const key = typeof eventName === "string" || typeof eventName === "symbol" || typeof eventName === "number" ? eventName : anyProducer;
  const producers = producersMap.get(instance);
  if (!producers.has(key)) {
    return;
  }
  return producers.get(key);
}
function enqueueProducers(instance, eventName, eventData) {
  const producers = producersMap.get(instance);
  if (producers.has(eventName)) {
    for (const producer of producers.get(eventName)) {
      producer.enqueue(eventData);
    }
  }
  if (producers.has(anyProducer)) {
    const item = Promise.all([eventName, eventData]);
    for (const producer of producers.get(anyProducer)) {
      producer.enqueue(item);
    }
  }
}
function iterator(instance, eventNames) {
  eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
  let isFinished = false;
  let flush = () => {
  };
  let queue = [];
  const producer = {
    enqueue(item) {
      queue.push(item);
      flush();
    },
    finish() {
      isFinished = true;
      flush();
    }
  };
  for (const eventName of eventNames) {
    let set = getEventProducers(instance, eventName);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      const producers = producersMap.get(instance);
      producers.set(eventName, set);
    }
    set.add(producer);
  }
  return {
    async next() {
      if (!queue) {
        return { done: true };
      }
      if (queue.length === 0) {
        if (isFinished) {
          queue = void 0;
          return this.next();
        }
        await new Promise((resolve) => {
          flush = resolve;
        });
        return this.next();
      }
      return {
        done: false,
        value: await queue.shift()
      };
    },
    async return(value) {
      queue = void 0;
      for (const eventName of eventNames) {
        const set = getEventProducers(instance, eventName);
        if (set) {
          set.delete(producer);
          if (set.size === 0) {
            const producers = producersMap.get(instance);
            producers.delete(eventName);
          }
        }
      }
      flush();
      return arguments.length > 0 ? { done: true, value: await value } : { done: true };
    },
    [Symbol.asyncIterator]() {
      return this;
    }
  };
}
function defaultMethodNamesOrAssert(methodNames) {
  if (methodNames === void 0) {
    return allEmitteryMethods;
  }
  if (!Array.isArray(methodNames)) {
    throw new TypeError("`methodNames` must be an array of strings");
  }
  for (const methodName of methodNames) {
    if (!allEmitteryMethods.includes(methodName)) {
      if (typeof methodName !== "string") {
        throw new TypeError("`methodNames` element must be a string");
      }
      throw new Error(`${methodName} is not Emittery method`);
    }
  }
  return methodNames;
}
const isMetaEvent = (eventName) => eventName === listenerAdded || eventName === listenerRemoved;
function emitMetaEvent(emitter, eventName, eventData) {
  if (isMetaEvent(eventName)) {
    try {
      canEmitMetaEvents = true;
      emitter.emit(eventName, eventData);
    } finally {
      canEmitMetaEvents = false;
    }
  }
}
class Emittery {
  static mixin(emitteryPropertyName, methodNames) {
    methodNames = defaultMethodNamesOrAssert(methodNames);
    return (target) => {
      if (typeof target !== "function") {
        throw new TypeError("`target` must be function");
      }
      for (const methodName of methodNames) {
        if (target.prototype[methodName] !== void 0) {
          throw new Error(`The property \`${methodName}\` already exists on \`target\``);
        }
      }
      function getEmitteryProperty() {
        Object.defineProperty(this, emitteryPropertyName, {
          enumerable: false,
          value: new Emittery()
        });
        return this[emitteryPropertyName];
      }
      Object.defineProperty(target.prototype, emitteryPropertyName, {
        enumerable: false,
        get: getEmitteryProperty
      });
      const emitteryMethodCaller = (methodName) => function(...args) {
        return this[emitteryPropertyName][methodName](...args);
      };
      for (const methodName of methodNames) {
        Object.defineProperty(target.prototype, methodName, {
          enumerable: false,
          value: emitteryMethodCaller(methodName)
        });
      }
      return target;
    };
  }
  static get isDebugEnabled() {
    var _a, _b;
    if (typeof ((_a = globalThis.process) == null ? void 0 : _a.env) !== "object") {
      return isGlobalDebugEnabled;
    }
    const { env } = (_b = globalThis.process) != null ? _b : { env: {} };
    return env.DEBUG === "emittery" || env.DEBUG === "*" || isGlobalDebugEnabled;
  }
  static set isDebugEnabled(newValue) {
    isGlobalDebugEnabled = newValue;
  }
  constructor(options = {}) {
    var _a;
    anyMap.set(this, /* @__PURE__ */ new Set());
    eventsMap.set(this, /* @__PURE__ */ new Map());
    producersMap.set(this, /* @__PURE__ */ new Map());
    producersMap.get(this).set(anyProducer, /* @__PURE__ */ new Set());
    this.debug = (_a = options.debug) != null ? _a : {};
    if (this.debug.enabled === void 0) {
      this.debug.enabled = false;
    }
    if (!this.debug.logger) {
      this.debug.logger = (type, debugName, eventName, eventData) => {
        try {
          eventData = JSON.stringify(eventData);
        } catch {
          eventData = `Object with the following keys failed to stringify: ${Object.keys(eventData).join(",")}`;
        }
        if (typeof eventName === "symbol" || typeof eventName === "number") {
          eventName = eventName.toString();
        }
        const currentTime = new Date();
        const logTime = `${currentTime.getHours()}:${currentTime.getMinutes()}:${currentTime.getSeconds()}.${currentTime.getMilliseconds()}`;
        console.log(`[${logTime}][emittery:${type}][${debugName}] Event Name: ${eventName}
	data: ${eventData}`);
      };
    }
  }
  logIfDebugEnabled(type, eventName, eventData) {
    if (Emittery.isDebugEnabled || this.debug.enabled) {
      this.debug.logger(type, this.debug.name, eventName, eventData);
    }
  }
  on(eventNames, listener) {
    assertListener(listener);
    eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
    for (const eventName of eventNames) {
      assertEventName(eventName);
      let set = getListeners(this, eventName);
      if (!set) {
        set = /* @__PURE__ */ new Set();
        const events = eventsMap.get(this);
        events.set(eventName, set);
      }
      set.add(listener);
      this.logIfDebugEnabled("subscribe", eventName, void 0);
      if (!isMetaEvent(eventName)) {
        emitMetaEvent(this, listenerAdded, { eventName, listener });
      }
    }
    return this.off.bind(this, eventNames, listener);
  }
  off(eventNames, listener) {
    assertListener(listener);
    eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
    for (const eventName of eventNames) {
      assertEventName(eventName);
      const set = getListeners(this, eventName);
      if (set) {
        set.delete(listener);
        if (set.size === 0) {
          const events = eventsMap.get(this);
          events.delete(eventName);
        }
      }
      this.logIfDebugEnabled("unsubscribe", eventName, void 0);
      if (!isMetaEvent(eventName)) {
        emitMetaEvent(this, listenerRemoved, { eventName, listener });
      }
    }
  }
  once(eventNames) {
    let off_;
    const promise = new Promise((resolve) => {
      off_ = this.on(eventNames, (data) => {
        off_();
        resolve(data);
      });
    });
    promise.off = off_;
    return promise;
  }
  events(eventNames) {
    eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
    for (const eventName of eventNames) {
      assertEventName(eventName);
    }
    return iterator(this, eventNames);
  }
  async emit(eventName, eventData) {
    var _a;
    assertEventName(eventName);
    if (isMetaEvent(eventName) && !canEmitMetaEvents) {
      throw new TypeError("`eventName` cannot be meta event `listenerAdded` or `listenerRemoved`");
    }
    this.logIfDebugEnabled("emit", eventName, eventData);
    enqueueProducers(this, eventName, eventData);
    const listeners = (_a = getListeners(this, eventName)) != null ? _a : /* @__PURE__ */ new Set();
    const anyListeners = anyMap.get(this);
    const staticListeners = [...listeners];
    const staticAnyListeners = isMetaEvent(eventName) ? [] : [...anyListeners];
    await resolvedPromise;
    await Promise.all([
      ...staticListeners.map(async (listener) => {
        if (listeners.has(listener)) {
          return listener(eventData);
        }
      }),
      ...staticAnyListeners.map(async (listener) => {
        if (anyListeners.has(listener)) {
          return listener(eventName, eventData);
        }
      })
    ]);
  }
  async emitSerial(eventName, eventData) {
    var _a;
    assertEventName(eventName);
    if (isMetaEvent(eventName) && !canEmitMetaEvents) {
      throw new TypeError("`eventName` cannot be meta event `listenerAdded` or `listenerRemoved`");
    }
    this.logIfDebugEnabled("emitSerial", eventName, eventData);
    const listeners = (_a = getListeners(this, eventName)) != null ? _a : /* @__PURE__ */ new Set();
    const anyListeners = anyMap.get(this);
    const staticListeners = [...listeners];
    const staticAnyListeners = [...anyListeners];
    await resolvedPromise;
    for (const listener of staticListeners) {
      if (listeners.has(listener)) {
        await listener(eventData);
      }
    }
    for (const listener of staticAnyListeners) {
      if (anyListeners.has(listener)) {
        await listener(eventName, eventData);
      }
    }
  }
  onAny(listener) {
    assertListener(listener);
    this.logIfDebugEnabled("subscribeAny", void 0, void 0);
    anyMap.get(this).add(listener);
    emitMetaEvent(this, listenerAdded, { listener });
    return this.offAny.bind(this, listener);
  }
  anyEvent() {
    return iterator(this);
  }
  offAny(listener) {
    assertListener(listener);
    this.logIfDebugEnabled("unsubscribeAny", void 0, void 0);
    emitMetaEvent(this, listenerRemoved, { listener });
    anyMap.get(this).delete(listener);
  }
  clearListeners(eventNames) {
    eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
    for (const eventName of eventNames) {
      this.logIfDebugEnabled("clear", eventName, void 0);
      if (typeof eventName === "string" || typeof eventName === "symbol" || typeof eventName === "number") {
        const set = getListeners(this, eventName);
        if (set) {
          set.clear();
        }
        const producers = getEventProducers(this, eventName);
        if (producers) {
          for (const producer of producers) {
            producer.finish();
          }
          producers.clear();
        }
      } else {
        anyMap.get(this).clear();
        for (const [eventName2, listeners] of eventsMap.get(this).entries()) {
          listeners.clear();
          eventsMap.get(this).delete(eventName2);
        }
        for (const [eventName2, producers] of producersMap.get(this).entries()) {
          for (const producer of producers) {
            producer.finish();
          }
          producers.clear();
          producersMap.get(this).delete(eventName2);
        }
      }
    }
  }
  listenerCount(eventNames) {
    var _a, _b, _c, _d, _e, _f;
    eventNames = Array.isArray(eventNames) ? eventNames : [eventNames];
    let count = 0;
    for (const eventName of eventNames) {
      if (typeof eventName === "string") {
        count += anyMap.get(this).size + ((_b = (_a = getListeners(this, eventName)) == null ? void 0 : _a.size) != null ? _b : 0) + ((_d = (_c = getEventProducers(this, eventName)) == null ? void 0 : _c.size) != null ? _d : 0) + ((_f = (_e = getEventProducers(this)) == null ? void 0 : _e.size) != null ? _f : 0);
        continue;
      }
      if (typeof eventName !== "undefined") {
        assertEventName(eventName);
      }
      count += anyMap.get(this).size;
      for (const value of eventsMap.get(this).values()) {
        count += value.size;
      }
      for (const value of producersMap.get(this).values()) {
        count += value.size;
      }
    }
    return count;
  }
  bindMethods(target, methodNames) {
    if (typeof target !== "object" || target === null) {
      throw new TypeError("`target` must be an object");
    }
    methodNames = defaultMethodNamesOrAssert(methodNames);
    for (const methodName of methodNames) {
      if (target[methodName] !== void 0) {
        throw new Error(`The property \`${methodName}\` already exists on \`target\``);
      }
      Object.defineProperty(target, methodName, {
        enumerable: false,
        value: this[methodName].bind(this)
      });
    }
  }
}
const allEmitteryMethods = Object.getOwnPropertyNames(Emittery.prototype).filter((v) => v !== "constructor");
Object.defineProperty(Emittery, "listenerAdded", {
  value: listenerAdded,
  writable: false,
  enumerable: true,
  configurable: false
});
Object.defineProperty(Emittery, "listenerRemoved", {
  value: listenerRemoved,
  writable: false,
  enumerable: true,
  configurable: false
});
function isInstanceOfEmittery(instance) {
  return instance.onAny && instance.emit;
}
function isChildClassOfEmittery(input) {
  return input.prototype.onAny && input.prototype.emit;
}
let nextId = 0;
async function createWorkerized(workerizedClass, worker, resolvers) {
  const isEmittery = isChildClassOfEmittery(workerizedClass);
  const result = isEmittery ? new Emittery() : {};
  const id = nextId++;
  Object.getOwnPropertyNames(workerizedClass.prototype).forEach(
    (functionName) => {
      result[functionName] = (...args) => postCommand$1(worker, CommandType.FORWARD, functionName, args, resolvers);
    }
  );
  result.terminate = async (...args) => {
    const response = await postCommand$1(
      worker,
      CommandType.TERMINATE,
      "",
      args,
      resolvers
    );
    delete workerized[id];
    worker.terminate();
    result.workerContext.worker = void 0;
    return response;
  };
  result.workerContext = {
    id,
    worker,
    resolvers
  };
  workerized[id] = result;
  return result;
}
async function workerize(workerizedClass, workerClass) {
  const resolvers = /* @__PURE__ */ new Map();
  let workerized2;
  const worker = await createWorker(
    workerizedClass,
    workerClass,
    resolvers,
    (name, data) => {
      if (workerized2 == null ? void 0 : workerized2.emit) {
        workerized2 == null ? void 0 : workerized2.emit(name, data);
      }
    }
  );
  workerized2 = await createWorkerized(
    workerizedClass,
    worker,
    resolvers
  );
  return workerized2;
}
async function handleCommandForward(command, context) {
  const { functionName, args } = command;
  if (!context.instance) {
    throw "instance not initialized";
  }
  if (!functionName) {
    throw "missing function name to call";
  }
  if (!context.instance[functionName]) {
    throw `undefined function [${functionName}] in class ${context.instance.constructor.workerId}`;
  }
  postCommand(command, await context.instance[functionName](...args != null ? args : []));
}
const registeredWorkers = createGlobalThisVariable("registeredWorkers", {});
function registerWorker(id, registrable) {
  registrable.workerId = id;
  if (isWorker()) {
    registeredWorkers[registrable.workerId] = registrable;
  }
}
function handleCommandInit(command, context) {
  if (!command.args) {
    throw "Missing className while initializing worker";
  }
  const [className, globalsValues] = command.args;
  const constructor = registeredWorkers[className];
  if (constructor) {
    context.instance = new constructor(command.args.slice(1));
  } else {
    throw `unknown worker class ${className}`;
  }
  copy(globalsValues, globals);
  if (isInstanceOfEmittery(context.instance)) {
    context.instance.onAny((name, data) => {
      postCommand(
        {
          type: CommandType.EVENT
        },
        { name, data }
      );
    });
  }
  postCommand(command, typeof context.instance !== void 0);
}
async function handleCommandTerminate(command, context) {
  const { args } = command;
  if (!context.instance) {
    throw "instance not initialized";
  }
  let result;
  if (context.instance.terminate) {
    result = await context.instance.terminate(...args != null ? args : []);
  }
  postCommand(command, result);
}
function handleCommandGlobalsSync(command) {
  if (!command.args) {
    throw "Missing globals while syncing";
  }
  copy(command.args[0], globals);
  postCommand(command, {});
}
function initWorker() {
  const context = {};
  onmessage = async (event) => {
    const command = event.data;
    switch (command.type) {
      case CommandType.INIT:
        handleCommandInit(command, context);
        break;
      case CommandType.FORWARD:
        handleCommandForward(command, context);
        break;
      case CommandType.TERMINATE:
        handleCommandTerminate(command, context);
        break;
      case CommandType.GLOBALS_SYNC:
        handleCommandGlobalsSync(command);
        break;
    }
  };
}
if (isWorker()) {
  initWorker();
}
function createGlobal(name, defaultValue) {
  if (!globals[name]) {
    globals[name] = defaultValue;
  }
  return [
    () => globals[name],
    async (value) => {
      globals[name] = value;
      await globalsSync();
    }
  ];
}
function registerGlobal(name, defaultValue) {
  return createGlobal(name, defaultValue);
}
export {
  CommandType,
  initWorker,
  registerGlobal,
  registerWorker,
  registeredWorkers,
  workerize
};
